import React from "react";
import { getPaymentMethodDisplay } from "@features/subscription/payment-method-label";
import { type QuoteModalState, type SubscriptionQuoteModalProps } from "@features/subscription/useSubscriptionCheckout";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@shared/ui/components";

const PLAN_LABELS: Record<"basic" | "pro", string> = {
  basic: "Базовый",
  pro: "Профи"
};

const PERIOD_LABELS: Record<"month" | "year", string> = {
  month: "месячный",
  year: "годовой"
};

const formatRub = (value: number): string => `${Math.round(value).toLocaleString("ru-RU")} ₽`;

const formatDate = (value: string): string => new Date(value).toLocaleDateString("ru-RU");

const renderTitle = (state: QuoteModalState): string =>
  state.kind === "upgrade" ? "Подтверждение оплаты" : "Переход на другой тариф";

const renderDescription = (state: QuoteModalState): string => {
  if (state.kind === "upgrade") {
    return state.quote.credit > 0
      ? `К оплате ${formatRub(state.quote.amount)} (зачтено ${formatRub(state.quote.credit)} за неиспользованную часть тарифа)`
      : `К оплате ${formatRub(state.quote.amount)}`;
  }

  return `Переход на ${PLAN_LABELS[state.plan]} ${PERIOD_LABELS[state.period]} с ${formatDate(
    state.quote.effectiveDate
  )}. Списаний сейчас нет — до этой даты действует текущий тариф.`;
};

export const SubscriptionQuoteModal: React.FC<SubscriptionQuoteModalProps> = ({
  state,
  isProcessing,
  onConfirm,
  onClose,
  failureMessage,
  onPayAnotherWay
}) => {
  const card = state?.kind === "upgrade" ? state.card : null;
  const hasCard = card?.hasCard === true;
  return (
    <Dialog open={state !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        {state && (
          <>
            <DialogHeader>
              <DialogTitle>{renderTitle(state)}</DialogTitle>
              <DialogDescription>{renderDescription(state)}</DialogDescription>
            </DialogHeader>

            {hasCard && card && !failureMessage && state.kind === "upgrade" && (
              <p className="text-sm text-muted-foreground">
                Спишем {formatRub(state.quote.amount)} с «{getPaymentMethodDisplay(card)}». Тариф включится сразу.
              </p>
            )}

            {failureMessage && (
              <div className="text-sm bg-destructive/10 p-3 rounded-md space-y-2">
                <p className="text-destructive">{failureMessage}</p>
                {onPayAnotherWay && (
                  <Button variant="outline" size="sm" onClick={onPayAnotherWay} disabled={isProcessing}>
                    Оплатить другой картой
                  </Button>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isProcessing}>
                Отмена
              </Button>
              <Button onClick={onConfirm} disabled={isProcessing}>
                {state.kind === "upgrade"
                  ? isProcessing
                    ? "Оплата..."
                    : hasCard
                      ? `Оплатить ${formatRub(state.quote.amount)}`
                      : "Оплатить"
                  : isProcessing
                    ? "Планирование..."
                    : "Запланировать"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
